import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { Box, Button, Skeleton } from '@mui/material';
import { useQuery } from 'react-query';
import { FundCarousel, FundCarouselCard } from 'src/components/FundCarousel';
import {
  getMetaCarteirasFicFidcsCarousel,
  MetaCarteirasFicFidcsCarouselItem,
  registerMetaCarteirasFicFidcsCarouselHit,
} from 'src/services/meta-carteiras-fic-fidcs';

const HOME_CAROUSEL_PARAMS = {
  limit: 15,
  orderBy: 'pl',
} as const;

function isValidCarouselItem(item: MetaCarteirasFicFidcsCarouselItem) {
  return Boolean(item.operationId?.trim() && item.name?.trim());
}

function FicFidcsHomeCarouselSkeleton() {
  return (
    <FundCarousel
      title="Monitore os FICFIDCs"
      icon={<MonitorHeartIcon />}
      height="auto">
      {Array.from({ length: 6 }).map((_, index) => (
        <Box
          key={index}
          data-testid="fic-fidcs-home-carousel-skeleton-card"
          sx={{
            p: 1.5,
            bgcolor: 'background.paper',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 1,
          }}>
          <Skeleton width="90%" height={28} />
          <Skeleton width="100%" height={22} />
        </Box>
      ))}
    </FundCarousel>
  );
}

export default function FicFidcsHomeCarousel() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['meta-carteiras-fic-fidcs-home-carousel', HOME_CAROUSEL_PARAMS],
    queryFn: () => getMetaCarteirasFicFidcsCarousel(HOME_CAROUSEL_PARAMS),
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return <FicFidcsHomeCarouselSkeleton />;
  }

  const items = data?.data?.filter(isValidCarouselItem) || [];

  if (isError || items.length === 0) {
    return null;
  }

  const handleView = (operationId: string) => {
    window.open(
      `/monitor/wallet/view/${operationId}`,
      '_blank',
      'noopener,noreferrer',
    );
    void registerMetaCarteirasFicFidcsCarouselHit(operationId).catch(() => {
      // Hit registration is fire-and-forget and must not block navigation.
    });
  };

  return (
    <FundCarousel
      title="Monitore os FICFIDCs"
      icon={<MonitorHeartIcon />}
      height="auto"
      infinite={true}
      autoPlay={true}
      autoPlayInterval={3000}>
      {items.map((item) => (
        <FundCarouselCard key={item.operationId}>
          <FundCarouselCard.Title>{item.name}</FundCarouselCard.Title>
          <FundCarouselCard.Action>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleView(item.operationId)}
              aria-label={`Ver ${item.name}`}>
              Ver
            </Button>
          </FundCarouselCard.Action>
        </FundCarouselCard>
      ))}
    </FundCarousel>
  );
}
